import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, createTableColumn, makeStyles, tokens } from '@fluentui/react-components';
import type { SortDirection } from '@fluentui/react-components';

const useStyles = makeStyles({
  cell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: tokens.spacingVerticalM },
});

const PAGE_SIZE = 50;

export interface ColumnDefinition<T> {
  columnId: string;
  label: string;
  renderCell: (item: T) => ReactNode;
  compare?: (a: T, b: T) => number;
}

export const DataTable = <T,>({ items, columns, getRowId, page, pageCount: externalPageCount, totalItems: externalTotalItems, onPageChange, emptyMessage = 'No data available.', onRowClick }: { items: T[]; columns: ColumnDefinition<T>[]; getRowId: (item: T) => string; page?: number; pageCount?: number; totalItems?: number; onPageChange?: (page: number) => void; emptyMessage?: string; onRowClick?: (item: T) => void }) => {
  const styles = useStyles();
  const [sortState, setSortState] = useState<{ columnId: string; direction: SortDirection } | null>(null);
  const [internalPage, setInternalPage] = useState(1);

  const currentPage = page ?? internalPage;
  const handlePageChange = onPageChange ?? setInternalPage;

  const gridColumns = columns.map((column) => createTableColumn<T>({ columnId: column.columnId, compare: column.compare, renderHeaderCell: () => column.label, renderCell: column.renderCell }));

  // Sort all items based on current sort state
  const sortedItems = useMemo(() => {
    if (!sortState) return items;
    const col = columns.find(c => c.columnId === sortState.columnId);
    if (!col?.compare) return items;
    const sorted = [...items].sort(col.compare);
    return sortState.direction === 'descending' ? sorted.reverse() : sorted;
  }, [items, sortState, columns]);

  // Paginate after sorting
  const totalItems = externalTotalItems ?? sortedItems.length;
  const pageCount = externalPageCount ?? Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const paginatedItems = sortedItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (paginatedItems.length === 0 && sortedItems.length === 0) {
    return <p>{emptyMessage}</p>;
  }

  return (
    <div>
      <DataGrid
        items={paginatedItems}
        columns={gridColumns}
        getRowId={getRowId}
        sortable
        style={{ minWidth: '100%' }}
        onSortChange={(_e, nextSort) => {
          setSortState(nextSort.sortColumn ? { columnId: nextSort.sortColumn as string, direction: nextSort.sortDirection } : null);
          handlePageChange(1);
        }}
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => <DataGridHeaderCell className={styles.cell}>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<T>>
          {({ item, rowId }) => (
            <DataGridRow<T> key={rowId} onClick={() => onRowClick?.(item)}>
              {({ renderCell }) => <DataGridCell className={styles.cell} title={typeof renderCell(item) === 'string' ? renderCell(item) as string : undefined}>{renderCell(item)}</DataGridCell>}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
      <div className={styles.footer}>
        <span>{totalItems} record(s)</span>
        <div>
          <Button disabled={currentPage <= 1} onClick={() => handlePageChange(currentPage - 1)}>Previous</Button>
          <span style={{ padding: '0 12px' }}>Page {currentPage} / {pageCount}</span>
          <Button disabled={currentPage >= pageCount} onClick={() => handlePageChange(currentPage + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
};
